/**
 * @fileoverview recentEventsSlice — client-side recently-viewed events.
 *
 * Powers the `RECENT` block of the Search empty state (FRE-767). Persists to
 * `localStorage` so the list survives reloads and tab switches. PostHog is
 * analytics-only and does not surface view history to the frontend, so this
 * slice owns the history client-side.
 */
import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

const STORAGE_KEY = 'loci:recentEvents';
const MAX_RECENT_EVENTS = 5;

export interface RecentEvent {
  id: string;
  title: string;
  imageUrl?: string;
  venueName: string;
  date: string;
  /** Epoch milliseconds when this view was recorded. */
  viewedAt: number;
}

export interface RecentEventsState {
  events: RecentEvent[];
}

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const loadFromStorage = (): RecentEvent[] => {
  if (!isBrowser()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRecentEvent).slice(0, MAX_RECENT_EVENTS);
  } catch {
    // Corrupted storage — start fresh rather than crashing the store.
    return [];
  }
};

const isRecentEvent = (value: unknown): value is RecentEvent => {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<RecentEvent>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.venueName === 'string' &&
    typeof v.date === 'string' &&
    typeof v.viewedAt === 'number' &&
    (typeof v.imageUrl === 'undefined' || typeof v.imageUrl === 'string')
  );
};

/**
 * Persists the recent-events list to `localStorage`. Exported so the store
 * setup can subscribe to slice changes and call this after each commit —
 * keeping the reducers themselves pure (no side effects). Safe no-op on the
 * server or when storage is unavailable / full.
 */
export const persistRecentEventsToStorage = (events: RecentEvent[]): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
  } catch {
    // Quota exceeded or storage disabled — silent no-op (analytics is best-effort).
  }
};

/**
 * Empty initial state. We intentionally avoid `loadFromStorage()` at module
 * evaluation so importing `@loci/store` has no side effects
 * (`pkg-no-side-effects` rule). Hydrate via `preloadedState` at store
 * creation in the browser, or call `hydrateRecentEventsFromStorage()` once at
 * app bootstrap.
 */
const initialState: RecentEventsState = { events: [] };

/**
 * Reads any persisted recent events from `localStorage`. Safe to call from
 * the browser at app bootstrap to seed `preloadedState`. Returns an empty
 * slice on the server or when storage is unavailable.
 */
export const hydrateRecentEventsFromStorage = (): RecentEventsState => ({
  events: loadFromStorage(),
});

const recentEventsSlice = createSlice({
  name: 'recentEvents',
  initialState,
  reducers: {
    addRecentEvent: {
      reducer(state, action: PayloadAction<RecentEvent>) {
        const incoming = action.payload;
        const deduped = state.events.filter((event) => event.id !== incoming.id);
        state.events = [incoming, ...deduped].slice(0, MAX_RECENT_EVENTS);
      },
      prepare(event: Omit<RecentEvent, 'viewedAt'> & { viewedAt?: number }) {
        return {
          payload: {
            ...event,
            viewedAt: event.viewedAt ?? Date.now(),
          } as RecentEvent,
        };
      },
    },
    clearRecentEvents(state) {
      state.events = [];
    },
  },
});

export const { addRecentEvent, clearRecentEvents } = recentEventsSlice.actions;
export default recentEventsSlice.reducer;

/**
 * The slice is unconditionally registered in `createAppStore`, so
 * `state.recentEvents` is guaranteed to exist. We narrow `RootState` once
 * here so a future rename of the reducer key surfaces a type error rather
 * than silently returning `[]`.
 */
type RootStateWithRecentEvents = RootState & { recentEvents: RecentEventsState };

const selectRecentEventsSlice = (state: RootState): RecentEventsState =>
  (state as RootStateWithRecentEvents).recentEvents;

export const selectRecentEvents = createSelector(selectRecentEventsSlice, (slice) => slice.events);

export const RECENT_EVENTS_STORAGE_KEY = STORAGE_KEY;
export const RECENT_EVENTS_MAX = MAX_RECENT_EVENTS;
